import React, { useState } from 'react';
import { format, differenceInMinutes } from 'date-fns';
import './ReservationDetails.css';
import { useReservationById, useUpdateReservationStatus, useCancelReservation, useNoShowReservation, useNoShowFreeTable, useCancelReservationFreeTableApp } from '../../api/reservations';
import { useCustomerbyID } from '../../api/customers';
import { useRestaurant } from '../../api/restaurant';
import { sendEmail } from '../../api/emails';
import ReservationEditModal from '../../components/ReservationEditModal/ReservationEditModal';
import { useUpdateNoShow, useUser } from '../../api/users';
import { supabaseFreeTable } from '../../utils/supabase/clientFreeTable';
import { useAuth } from '../../api/AuthProvider';
import { supabase } from '../../utils/supabase/client';

const ReservationDetailsModal = ({ reservationId, tables, onClose, guestID }) => {
  const { data: reservation, isLoading, error, refetch } = useReservationById(reservationId);
  const { data: gostin, isLoading: isLoadingGuest } = useCustomerbyID(guestID);
  const { data: restaurantData, isLoading: isRestaurantLoading } = useRestaurant();
  const { mutate: updateStatus } = useUpdateReservationStatus();
  const { mutate: cancelReservation } = useCancelReservation();
  const { mutate: noShowReservation } = useNoShowReservation();
  const [showConfirm, setShowConfirm] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const { mutate: markNoShowFreeTable } = useNoShowFreeTable();
  const { mutate: CancelFreeTable } = useCancelReservationFreeTableApp();
  const { profile } = useAuth();
  const { mutate: cepkajRezervacii } = useUpdateNoShow();
  const { data: freeTableUser, isLoading: isLoadingUser } = useUser(
    reservation?.freeTableUser,
    {
      enabled: !!reservation?.freeTableUser,
    }
  );


  if (isLoading || isLoadingGuest || isRestaurantLoading || (isLoading && reservation?.freeTableUser)) {
 return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
        padding: '12px'
      }}>
        <div style={{
          background: '#ffffff',
          borderRadius: '8px',
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
          padding: 'clamp(1.5rem, 3vw, 2rem)',
          width: 'clamp(320px, 90vw, 480px)',
          maxWidth: '100%',
          border: '1px solid #e5e7eb',
          position: 'relative'
        }}>
          {/* Skeleton Title */}
          <div style={{
            height: '24px',
            width: '60%',
            background: '#e5e7eb',
            borderRadius: '4px',
            marginBottom: '16px',
            animation: 'pulse 1.5s infinite'
          }}></div>
          {/* Skeleton Detail Items */}
          {[...Array(8)].map((_, index) => (
            <div key={index} style={{
              display: 'flex',
              gap: '8px',
              marginBottom: '12px'
            }}>
              <div style={{
                width: '130px',
                height: '16px',
                background: '#e5e7eb',
                borderRadius: '4px',
                animation: 'pulse 1.5s infinite'
              }}></div>
              <div style={{
                flex: 1,
                height: '16px',
                background: '#e5e7eb',
                borderRadius: '4px',
                animation: 'pulse 1.5s infinite'
              }}></div>
            </div>
          ))}
          {/* Skeleton Buttons */}
          <div style={{
            display: 'flex',
            gap: '8px',
            flexWrap: 'wrap',
            marginTop: '16px'
          }}>
            {[...Array(3)].map((_, index) => (
              <div key={index} style={{
                height: '36px',
                width: '80px',
                background: '#e5e7eb',
                borderRadius: '6px',
                animation: 'pulse 1.5s infinite'
              }}></div>
            ))}
          </div>
          <style>
            {`
              @keyframes pulse {
                0% { opacity: 1; }
                50% { opacity: 0.6; }
                100% { opacity: 1; }
              }
            `}
          </style>
        </div>
      </div>
    );  }
  if (error) {
    return <div className="modal-overlay"><div className="modal">Грешка: {error.message}</div></div>;
  }
  if (!reservation) return null;

  // Filter tables based on reservation.tableIds
  const assignedTables = tables.filter(table => reservation.tableIds.includes(table.id));
  const totalSeatsAssigned = assignedTables.reduce((sum, table) => sum + table.seats, 0);
  const totalSeatsUsed = Math.min(reservation.partySize, totalSeatsAssigned);
  const date = new Date(reservation.time);
  const dateString = format(date, 'dd.MM.yyyy');
  const timeString = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });

  const currentTime = new Date();
  const minutesSinceReservation = differenceInMinutes(currentTime, date);
  const canMarkNoShow = reservation.status === 'pending' && minutesSinceReservation >= 30;

  const convertDateToShow = (date) => {
    if (!date) return "Непознат Датум";
    const dateObj = new Date(date);
    return `${dateObj.getDate().toString().padStart(2, '0')}/${(dateObj.getMonth() + 1).toString().padStart(2, '0')}/${dateObj.getFullYear()}`;
  };

  const convertTimeToShow = (time) => {
    if (!time) return "Непознато Време";
    const [hour, minute] = time.split(':');
    return `${hour}:${minute}`;
  };

  const statusDisplay = {
    pending: '"Во чекање"',
    seated: 'Седната',
    finalized: 'Финализиран',
    canceled: 'Откажана',
    noshow: 'Не се појави',
    CONFIRMED:"Во чекање",
    DECLINED:"Одбиена",
    PENDING:"Чека потврда од гостин"
  };

  const handleAction = (action) => {
    if (action === 'edit') {
      setShowEditModal(true);
      return;
    }
    setShowConfirm({ action });
  };

  const handleEditSave = () => {
    refetch();
  };

  const confirmAction = async () => {
    const { action } = showConfirm;
    try {
      if (action === 'check-in') {
        await new Promise((resolve, reject) => {
          updateStatus(
            { reservationId, status: 'seated' },
            {
              onSuccess: () => resolve(),
              onError: (err) => reject(new Error(`Грешка при пријавување: ${err.message}`)),
            }
          );
        });
        setShowConfirm(null);
      } else if (action === 'check-out') {
        await new Promise((resolve, reject) => {
          updateStatus(
            { reservationId, status: 'finalized' },
            {
              onSuccess: () => resolve(),
              onError: (err) => reject(new Error(`Грешка при одјавување: ${err.message}`)),
            }
          );
        });
        setShowConfirm(null);
      } else if (action === 'cancel') {
        await new Promise((resolve, reject) => {
          if (reservation.freeTableId && freeTableUser) {
            CancelFreeTable({ id: reservation.freeTableId });
            sendPushNotification(
              freeTableUser.expo_push_token,
              `Вашата резервација во ${restaurantData.name} беше откажана`,
              `Вашата резервација во ${restaurantData.name} беше откажана од страна на ресторанот. Доколку мислите дека ова е грешка исконтактирајте го ресторанот директно!`
            );
          }
          cancelReservation(
            { reservationId },
            {
              onSuccess: () => resolve(),
              onError: (err) => reject(new Error(`Грешка при откажување: ${err.message}`)),
            }
          );
        });

        if (gostin?.email) {
          const reservationTime = format(new Date(reservation.time), 'HH:mm');
          const placeholders = {
            'Guest Name': reservation.name || 'Guest',
            'Restaurant Name': restaurantData?.name || 'Our Restaurant',
            'Reservation Date': convertDateToShow(reservation.time),
            'Reservation Time': convertTimeToShow(reservationTime),
            'Number of Guests': reservation.partySize,
            'Restaurant Address': restaurantData?.address || 'Unknown Address',
            'Contact Info': restaurantData?.contactInfo || 'contact@freetable.mk',
          };

          const sendResult = await sendEmail({
            restaurantId: profile.restaurantid,
            templateKey: 'canceled',
            language: reservation.language,
            recipient: gostin.email,
            placeholders,
          });

          if (!sendResult.success) {
            console.warn('Failed to send cancellation email:', sendResult.error);
            setErrorMessage(`Резервацијата е откажана, но грешка при испраќање на имейлот: ${sendResult.error}`);
          }
        }

        setShowConfirm(null);
        onClose();
      } else if (action === 'noshow') {
        await new Promise((resolve, reject) => {
          if (reservation.freeTableId && freeTableUser) {
            markNoShowFreeTable({ id: reservation.freeTableId });
            cepkajRezervacii({ userid: freeTableUser.id, reservationsleft: freeTableUser.no_shows_left - 1 });
            sendPushNotification(
              freeTableUser.expo_push_token,
              `Вашата резервација во ${restaurantData.name} беше означена како неостварена`,
              freeTableUser.no_shows_left - 1 > 0
                ? `Вашата резервација во ${restaurantData.name} е означена како непотврдена. Имате уште ${freeTableUser.no_shows_left - 1} резервации до суспензија на профилот!`
                : `Вашиот профил е суспендиран поради голем број на неостварени резервации!`
            );
          }
          noShowReservation(
            { reservationId },
            {
              onSuccess: async () => {
          // Increment no_show_count in customers table
          if (gostin) {
            const { error: customerError } = await supabase
              .from('customers')
              .update({ no_show_count:  gostin.no_show_count+1})
              .eq('id', guestID);
            
            if (customerError) {
              console.warn('Failed to increment no-show count:', customerError.message);
              reject(new Error(`Грешка при ажурирање на бројот на неостварени резервации: ${customerError.message}`));
              return;
            }
          }
          resolve();
        },
              onError: (err) => reject(new Error(`Грешка при означување како не се појави: ${err.message}`)),
            }
          );
        });

        if (gostin?.email) {
          const reservationTime = format(new Date(reservation.time), 'HH:mm');
          const placeholders = {
            'Guest Name': reservation.name || 'Guest',
            'Restaurant Name': restaurantData?.name || 'Our Restaurant',
            'Reservation Date': convertDateToShow(reservation.time),
            'Reservation Time': convertTimeToShow(reservationTime),
            'Number of Guests':reservation.partySize, 
            'Contact Info': restaurantData?.contactInfo || 'contact@freetable.mk',
          };

          const sendResult = await sendEmail({
            restaurantId: profile.restaurantid,
            templateKey: 'noShow',
            language: reservation.language,
            recipient: gostin.email,
            placeholders,
          });

          if (!sendResult.success) {
            console.warn('Failed to send noshow email:', sendResult.error);
            setErrorMessage(`Резервацијата е означена како не се појави, но грешка при испраќање на имейлот: ${sendResult.error}`);
          }
        }

        setShowConfirm(null);
        onClose();
      }
    } catch (err) {
      setErrorMessage(err.message);
    }
  };

  const actionDisplay = {
    'check-in': 'ја пријавите',
    'check-out': 'ја финализирате',
    'cancel': 'ја откажете',
    'noshow': 'ја означите како неостарена',
  };

  async function sendPushNotification(token, title, body) {
    try {
      const { data, error } = await supabaseFreeTable.functions.invoke('sendPushNotification', {
        body: { token, title, body },
      });

      if (error) {
        console.error('Error sending push notification:', error);
        return;
      }

    } catch (error) {
      console.error('Unexpected error:', error);
    }
  }

  if (showEditModal) {
    return (
      <ReservationEditModal
        reservationId={reservationId}
        tables={tables}
        onClose={() => setShowEditModal(false)}
        guestID={guestID}
        onSave={handleEditSave}
      />
    );
  }

  return (
    <div className="modal-overlay">
      <div className="modal">
        <button className="modal-close" onClick={onClose}>×</button>
        <h3>Информации за резервација</h3>
        <div className="detail-item">
          <span>Дата:</span> {dateString}
        </div>
        <div className="detail-item">
          <span>Време:</span> {timeString}
        </div>
        <div className="detail-item">
          <span>Име:</span> {reservation.name}
        </div>
        {reservation.discount && (
          <div className="detail-item">
            <span>Попуст:</span> {reservation.discount} %
          </div>
        )}
        <div className="detail-item">
          <span>Телефонски број:</span> {gostin?.phone || 'Нема информации'}
        </div>
        <div className="detail-item">
          <span>Емаил адреса:</span> {gostin?.email || 'Нема информации'}
        </div>
        <div className="detail-item">
          <span>Број на луѓе:</span> {reservation.partySize}
        </div>
        {reservation.additionalRequest && (
          <div className="detail-item">
            <span>Дополнително барање:</span> {reservation.additionalRequest}
          </div>
        )}
        <div className="detail-item">
          <span>{reservation.status == "DECLINED"?"Одбиено":"Потврденo"} од:</span> {reservation.confirmedBy||"N/A"}
        </div>
        
        {reservation.prefered_place && (
          <div className="detail-item">
            <span>Место за седење:</span> {reservation.prefered_place}
          </div>
        )}
        <div className="detail-item">
          <span>Статус:</span> {statusDisplay[reservation.status] || reservation.status}
        </div>
        <div className="detail-item">
          <span>Број на маса:</span> 
          {assignedTables.length > 0 
            ? assignedTables.map(t => t.display_number || t.display_number).join(', ') 
            : 'Нема'}
        </div>
        {errorMessage && (
          <div className="error-message">
            {errorMessage}
          </div>
        )}
        <div className="detail-actions">
          {reservation.status !== "canceled" && reservation.status !== "noshow" && reservation.status !== "finalized" &&reservation.status!=="DECLINED" &&reservation.status!=="PENDING"  && (
            <button
              className="edit-button"
              onClick={() => handleAction('edit')}
            >
              Измени
            </button>
          )}
          {reservation.status === 'pending' && (
            <button
              className="check-in-button"
              onClick={() => handleAction('check-in')}
            >
              Пријави
            </button>
          )}
          {reservation.status === 'seated' && (
            <button
              className="check-out-button"
              onClick={() => handleAction('check-out')}
            >
              Одјави
            </button>
          )}
          {reservation.status === 'pending' && (
            <button
              className="cancel-button"
              onClick={() => handleAction('cancel')}
            >
              Откажи
            </button>
          )}
          {canMarkNoShow && (
            <button
              className="noshow-button"
              onClick={() => handleAction('noshow')}
            >
              Не се појави
            </button>
          )}
        </div>
        {showConfirm && (
          <div className="confirm-prompt">
            <p>Дали сте сигурни дека сакате да {actionDisplay[showConfirm.action]} оваа резервација?</p>
            <div className="confirm-actions">
              <button onClick={confirmAction}>Да</button>
              <button onClick={() => setShowConfirm(null)}>Не</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReservationDetailsModal;